import { useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { NavigationProps, Chip, NavItem } from '@resiliatech/birdsong'
import { useAppState } from 'Context/AppContext/AppContext'
import { analytics } from 'Modules/Analytics'
import { navigation } from 'Modules/Analytics/trackActionInterface/Navigation'
import { FeatureGatingLimit } from 'Generated/graphql-types'
import {
  useAppNavigationConfig,
  NavItemConfiguration,
  SubNavItemConfiguration,
} from './useAppNavigationConfig'

const generateIdForItem = (
  item: NavItemConfiguration | SubNavItemConfiguration
) => item.label.toLowerCase().replace(' ', '-')
const isAppRoute = (route: string) => route && !route.startsWith('http')

/**
 * Given a set of feature gating limits and the name of a feature gating rule,
 * return the name of the minimum required plan tier needed to access this feature
 * IF the user does not have access to the feature.
 */
const getRequiredPlanName = (
  featureGatingLimit: FeatureGatingLimit[] | null,
  featureGatingRule: string
): string | null | undefined =>
  (featureGatingLimit || [])
    .map((featureLimit: FeatureGatingLimit) => {
      if (
        featureLimit.featureName === featureGatingRule &&
        !featureLimit.visible
      ) {
        const name = featureLimit.lowestPlanTier || ''

        return (
          name.toLowerCase().charAt(0).toUpperCase() +
          name.toLowerCase().slice(1)
        )
      }

      return null
    })
    .filter(Boolean)[0]

/*
 * This hook takes the app navigation configuration from useAppNavigationConfig
 * and translates the items into props for the presentational component
 */
export const useAppNavigationProps = (): Pick<
  NavigationProps,
  'navItems' | 'accountItems' | 'activeItemId'
> => {
  const { navItems, accountItems } = useAppNavigationConfig()
  const { pathname } = useLocation()
  const history = useHistory()

  const {
    userConfig: { permissions: userPermissions },
    ui: { featureGatingLimit },
  } = useAppState()

  const filterByPermissions = (
    item: NavItemConfiguration | SubNavItemConfiguration
  ) =>
    item.permissions.every((permission) =>
      userPermissions?.includes(permission)
    )

  const filterSubItemsByPermissions = (item: NavItemConfiguration) => ({
    ...item,
    ...(item.subNavItems && {
      subNavItems: item.subNavItems.filter(filterByPermissions),
    }),
    ...(item.subMenuItems && {
      subMenuItems: item.subMenuItems.filter(filterByPermissions),
    }),
  })

  const transformToCommonNavProps = (
    item: NavItemConfiguration | SubNavItemConfiguration
  ): Pick<
    NavItem,
    | 'id'
    | 'label'
    | 'chip'
    | 'href'
    | 'spawn'
    | 'allowClickDefault'
    | 'handleClick'
    | 'TrailingIcon'
    | 'subMenuItems'
  > => {
    const requiredPlanName = item.featureGatingRule
      ? getRequiredPlanName(featureGatingLimit, item.featureGatingRule)
      : undefined
    const hasClickBehavior =
      Boolean(item.eventConfig) ||
      Boolean(item.handleClick) ||
      Boolean(item.route)

    return {
      id: generateIdForItem(item),
      label: item.label,
      ...(item.new && {
        chip: <Chip text='New' variant='success' />,
      }),
      ...(requiredPlanName && {
        chip: <Chip text={requiredPlanName} variant='warning' />,
      }),
      href: item.route,
      TrailingIcon: item.TrailingIcon,
      spawn: item.spawn,
      allowClickDefault: Boolean(item.route) && !isAppRoute(item.route || ''),
      ...(item.subMenuItems && {
        subMenuItems: item.subMenuItems
          .filter(filterByPermissions)
          .map(filterSubItemsByPermissions)
          .map(transformToNavigationProps),
      }),
      ...(hasClickBehavior
        ? {
            handleClick: (event) => {
              if (!item.noclick) {
                const dispatch = analytics.trackActionCreator(
                  navigation.Item_Clicked
                )
                dispatch({
                  label: item.label,
                })
              }

              if (item.eventConfig) {
                const dispatch = analytics.trackActionCreator(item.eventConfig)
                dispatch({})
              }

              if (item.handleClick) {
                item.handleClick(event)
              }

              if (item.route && isAppRoute(item.route)) {
                history.push(item.route)
              }
            },
          }
        : {}),
    }
  }

  const transformToNavigationProps = (item: NavItemConfiguration): NavItem => {
    const base = {
      ...transformToCommonNavProps(item),
      Icon: item.Icon,
    }
    return base
  }

  // Define route groups that should stay highlighted for their sub-routes
  type RouteGroupConfig = {
    baseRoute: string
    excludeRoutes: string[]
    includeRoutes?: string[]
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const routeGroups: Record<string, RouteGroupConfig> = {
    academy: {
      baseRoute: '/academy',
      excludeRoutes: ['/academy/1-1-coaching'],
    },
    coaching: {
      baseRoute: '/coaching',
      excludeRoutes: [],
      includeRoutes: ['/academy/1-1-coaching'],
    },
    donations: {
      baseRoute: '/fundraising/donations',
      excludeRoutes: [],
    },
    fundraising: {
      baseRoute: '/fundraising',
      excludeRoutes: [
        '/fundraising/donations',
        '/fundraising/donations/campaigns',
        '/fundraising/donations/transactions',
        '/fundraising/donations/payouts',
      ],
    },
    impact: {
      baseRoute: '/impact',
      excludeRoutes: ['/impact/stories'],
    },
  }

  // Determine which navigation item is currently selected by checking the
  // current app route against all the app routes in the navigation config
  const activeItemId = useMemo(() => {
    const itemsWithAppRoute = [...navItems, ...accountItems]
      .flatMap((item) => [
        item,
        ...(item.subNavItems || item.subMenuItems || []),
      ])
      .filter((item) => item.route && isAppRoute(item.route))

    const parentMatch = itemsWithAppRoute.find((item) => {
      if (!item.route) return false

      // Find the matching route group
      const routeGroup = Object.entries(routeGroups).find(([, config]) =>
        item.route?.startsWith(config.baseRoute)
      )

      if (routeGroup) {
        const [, config] = routeGroup
        // Check if the current path matches the base route or its sub-routes
        // but exclude any routes in the excludeRoutes array
        // Also check if the current path is in the includeRoutes array
        return (
          (pathname.startsWith(config.baseRoute) &&
            !config.excludeRoutes.some((excludeRoute) =>
              pathname.startsWith(excludeRoute)
            )) ||
          config.includeRoutes?.some((includeRoute) =>
            pathname.startsWith(includeRoute)
          ) ||
          false
        )
      }

      return false
    })

    if (parentMatch) {
      return generateIdForItem(parentMatch)
    }

    const exactMatch = itemsWithAppRoute.find((item) => pathname === item.route)
    if (exactMatch) {
      return generateIdForItem(exactMatch)
    }

    return ''
  }, [navItems, accountItems, routeGroups, pathname])

  return {
    navItems: navItems
      .filter(filterByPermissions)
      .map(filterSubItemsByPermissions)
      .map(transformToNavigationProps),
    accountItems: accountItems
      .filter(filterByPermissions)
      .map(filterSubItemsByPermissions)
      .map(transformToNavigationProps),
    activeItemId,
  }
}
